import { useState, useEffect } from "react";
import { countCharacters } from "@root/shared/utils/countCharacters";
import { type CheckboxState, type ErrorItem, type TextAreaState } from "./type";

type UseValidationTextParams = {
  // Step2
  checkboxValues: CheckboxState;
  // Step3
  radioValues: Record<string, number | null>;
  // Step4
  textAreaState: TextAreaState;
  // Step5
  files: File[];
  railsValidationTexts: ErrorItem[];
  // Step1
  jobEntryValue?: string;
  emailValue?: string;
  businessTypeValue?: number;
  employmentValue?: number;
};

type ReturnType = {
  isValid: boolean;
  errorTexts: ErrorItem;
  updateClickStatus: () => void;
};

// フロント側バリデーション
export const useValidationText = ({
  checkboxValues,
  radioValues,
  textAreaState,
  files,
  railsValidationTexts,
  jobEntryValue,
  emailValue,
  businessTypeValue,
  employmentValue,
}: UseValidationTextParams): ReturnType => {
  // 送信ボタンを一度でも押したことがあるか
  const [isClicked, setIsClicked] = useState<boolean>(false);

  // エラー文言全体
  const [errorTexts, setErrorTexts] = useState<ErrorItem>({});

  // 送信ボタン押下時に一括errorTextsに反映できるよう、各項目ごとのエラー文言を個別で管理する
  // Step1
  // 基本情報(勤務先)
  const [jobEntryErrorText, setJobEntryErrorText] = useState<string | null>(null);
  // 基本情報(メールアドレス)
  const [emailErrorText, setEmailErrorText] = useState<string | null>(null);
  // 就業情報(就業状況)
  const [employmentStatusErrorText, setEmploymentStatusErrorText] = useState<string | null>(null);
  // 就業情報(職種)
  const [businessTypesErrorText, setBusinessTypesErrorText] = useState<string | null>(null);
  // 就業情報(雇用形態)
  const [employmentErrorText, setEmploymentErrorText] = useState<string | null>(null);

  // Step2
  // 待遇面の口コミ
  const [treatmentsErrorText, setTreatmentsErrorText] = useState<string | null>(null);
  // 社風面の口コミ
  const [culturesErrorText, setCulturesErrorText] = useState<string | null>(null);

  // Step3
  // 職場の口コミ(特徴)
  const [atmosphereErrorText, setAtmosphereErrorText] = useState<string | null>(null);
  const [salaryStandardErrorText, setSalaryStandardErrorText] = useState<string | null>(null);
  const [debutErrorText, setDebutErrorText] = useState<string | null>(null);
  const [educationErrorText, setEducationErrorText] = useState<string | null>(null);
  const [workTimeErrorText, setWorkTimeErrorText] = useState<string | null>(null);
  const [salaryChangeErrorText, setSalaryChangeErrorText] = useState<string | null>(null);

  // Step4
  // 職場の口コミ(あなたの考え/想い)
  const [workplaceMatchingErrorText, setWorkplaceMatchingErrorText] = useState<string | null>(null);
  const [interestRewardErrorText, setInterestRewardErrorText] = useState<string | null>(null);
  const [challengeErrorText, setChallengeErrorText] = useState<string | null>(null);
  const [expectationErrorText, setExpectationErrorText] = useState<string | null>(null);

  // Step5
  // 勤続証明書類
  const [filesErrorText, setFilesErrorText] = useState<string | null>(null);

  // バリデーション通過判定
  const [isValid, setIsValid] = useState(false);

  const CHECKBOX_MIN_LENGTH = 1;
  const CHECKBOX_MAX_LENGTH = 5;
  // 共通で使用するバリデーション文言
  const PLEASE_CHOOSE_VALIDATION_TEXT = "選択してください。";
  const PLEASE_FILL_VALIDATION_TEXT = "入力してください。";
  const MIN_LENGTH_VALIDATION_TEXT = `${CHECKBOX_MIN_LENGTH}つ以上選択してください。`;
  const MAX_LENGTH_VALIDATION_TEXT = `${CHECKBOX_MAX_LENGTH}つ以下にしてください。`;
  const TEXTAREA_CHARACTER_LENGTH_VALIDATION_TEXT = "100~1000字で入力してください。";

  const updateClickStatus = () => {
    setIsClicked(true);
  };

  // Rails側バリデーションエラー文言を反映する
  useEffect(() => {
    const errorsResponse: ErrorItem = {};
    railsValidationTexts?.forEach((errorMessage: ErrorItem) => {
      const key = Object.keys(errorMessage)[0];
      const message = errorMessage[key];
      errorsResponse[key] = message;
    });
    setErrorTexts(errorsResponse);
  }, [railsValidationTexts]);

  // Step1 基本情報(勤務先)
  useEffect(() => {
    if (!jobEntryValue) {
      setJobEntryErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setJobEntryErrorText(null);
  }, [jobEntryValue]);

  // Step1 基本情報(メールアドレス)
  useEffect(() => {
    if (!emailValue) {
      setEmailErrorText(PLEASE_FILL_VALIDATION_TEXT);
      return;
    }
    setEmailErrorText(null);
  }, [emailValue]);

  // Step1 就業情報(就業状況)
  useEffect(() => {
    if (!radioValues?.employmentStatus) {
      setEmploymentStatusErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setEmploymentStatusErrorText(null);
  }, [radioValues?.employmentStatus]);

  // Step1 就業情報(職種)
  useEffect(() => {
    if (!businessTypeValue) {
      setBusinessTypesErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setBusinessTypesErrorText(null);
  }, [businessTypeValue]);

  // Step1 就業情報(雇用形態)
  useEffect(() => {
    if (!employmentValue) {
      setEmploymentErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setEmploymentErrorText(null);
  }, [employmentValue]);

  // Step2 待遇面の口コミ
  useEffect(() => {
    if (!checkboxValues?.treatments || checkboxValues?.treatments.length === 0) {
      setTreatmentsErrorText(MIN_LENGTH_VALIDATION_TEXT);
      return;
    }
    if (checkboxValues?.treatments.length > CHECKBOX_MAX_LENGTH) {
      setTreatmentsErrorText(MAX_LENGTH_VALIDATION_TEXT);
      return;
    }
    setTreatmentsErrorText(null);
  }, [MAX_LENGTH_VALIDATION_TEXT, MIN_LENGTH_VALIDATION_TEXT, checkboxValues?.treatments]);

  // Step2 社風面の口コミ
  useEffect(() => {
    if (!checkboxValues?.cultures || checkboxValues?.cultures.length === 0) {
      setCulturesErrorText(MIN_LENGTH_VALIDATION_TEXT);
      return;
    }
    if (checkboxValues?.cultures.length > CHECKBOX_MAX_LENGTH) {
      setCulturesErrorText(MAX_LENGTH_VALIDATION_TEXT);
      return;
    }
    setCulturesErrorText(null);
  }, [MAX_LENGTH_VALIDATION_TEXT, MIN_LENGTH_VALIDATION_TEXT, checkboxValues?.cultures]);

  // Step3 雰囲気
  useEffect(() => {
    if (!radioValues?.atmosphere) {
      setAtmosphereErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setAtmosphereErrorText(null);
  }, [radioValues?.atmosphere]);

  // Step3 給与体系
  useEffect(() => {
    if (!radioValues?.salaryStandard) {
      setSalaryStandardErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setSalaryStandardErrorText(null);
  }, [radioValues?.salaryStandard]);

  // Step3 デビュー
  useEffect(() => {
    if (!radioValues?.debut) {
      setDebutErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setDebutErrorText(null);
  }, [radioValues?.debut]);

  // Step3 教育方針
  useEffect(() => {
    if (!radioValues?.education) {
      setEducationErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setEducationErrorText(null);
  }, [radioValues?.education]);

  // Step3 勤務時間
  useEffect(() => {
    if (!radioValues?.workTime) {
      setWorkTimeErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setWorkTimeErrorText(null);
  }, [radioValues?.workTime]);

  // Step3 給与の変化
  useEffect(() => {
    if (!radioValues?.salaryChange) {
      setSalaryChangeErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setSalaryChangeErrorText(null);
  }, [radioValues?.salaryChange]);

  // Step4 職場に合う人
  useEffect(() => {
    if (!textAreaState?.workplaceMatching) {
      setWorkplaceMatchingErrorText(PLEASE_FILL_VALIDATION_TEXT);
      return;
    }

    // 文字数のバリデーション
    const count = countCharacters(textAreaState?.workplaceMatching);
    if (count < 100 || count > 1000) {
      setWorkplaceMatchingErrorText(TEXTAREA_CHARACTER_LENGTH_VALIDATION_TEXT);
      return;
    }

    setWorkplaceMatchingErrorText(null);
  }, [textAreaState?.workplaceMatching, setWorkplaceMatchingErrorText]);

  // Step4 面白み・やりがい
  useEffect(() => {
    if (!textAreaState?.interestReward) {
      setInterestRewardErrorText(PLEASE_FILL_VALIDATION_TEXT);
      return;
    }

    // 文字数のバリデーション
    const count = countCharacters(textAreaState?.interestReward);
    if (count < 100 || count > 1000) {
      setInterestRewardErrorText(TEXTAREA_CHARACTER_LENGTH_VALIDATION_TEXT);
      return;
    }

    setInterestRewardErrorText(null);
  }, [textAreaState?.interestReward]);

  // Step4 挑戦できること
  useEffect(() => {
    if (!textAreaState?.challenge) {
      setChallengeErrorText(PLEASE_FILL_VALIDATION_TEXT);
      return;
    }

    // 文字数のバリデーション
    const count = countCharacters(textAreaState?.challenge);
    if (count < 100 || count > 1000) {
      setChallengeErrorText(TEXTAREA_CHARACTER_LENGTH_VALIDATION_TEXT);
      return;
    }

    setChallengeErrorText(null);
  }, [textAreaState?.challenge]);

  // Step4 期待すること
  useEffect(() => {
    if (!textAreaState?.expectation) {
      setExpectationErrorText(PLEASE_FILL_VALIDATION_TEXT);
      return;
    }

    // 文字数のバリデーション
    const count = countCharacters(textAreaState?.expectation);
    if (count < 100 || count > 1000) {
      setExpectationErrorText(TEXTAREA_CHARACTER_LENGTH_VALIDATION_TEXT);
      return;
    }

    setExpectationErrorText(null);
  }, [textAreaState?.expectation]);

  // Step5 勤務証明書類
  useEffect(() => {
    if (!files || files.length === 0) {
      setFilesErrorText(PLEASE_CHOOSE_VALIDATION_TEXT);
      return;
    }
    setFilesErrorText(null);
  }, [files]);

  // バリデーションエラー文言を反映する
  useEffect(() => {
    if (isClicked)
      setErrorTexts({
        // step1
        jobEntry: jobEntryErrorText || "",
        email: emailErrorText || "",
        employmentStatus: employmentStatusErrorText || "",
        businessType: businessTypesErrorText || "",
        employment: employmentErrorText || "",
        // step2
        treatments: treatmentsErrorText || "",
        cultures: culturesErrorText || "",
        // step3
        atmosphere: atmosphereErrorText || "",
        salaryStandard: salaryStandardErrorText || "",
        debut: debutErrorText || "",
        education: educationErrorText || "",
        workTime: workTimeErrorText || "",
        salaryChange: salaryChangeErrorText || "",
        // step4
        workplaceMatching: workplaceMatchingErrorText || "",
        interestReward: interestRewardErrorText || "",
        challenge: challengeErrorText || "",
        expectation: expectationErrorText || "",
        // step5
        files: filesErrorText || "",
      });
  }, [
    isClicked,
    // Step1
    jobEntryErrorText,
    emailErrorText,
    employmentStatusErrorText,
    businessTypesErrorText,
    employmentErrorText,
    // Step2
    treatmentsErrorText,
    culturesErrorText,
    // Step3
    atmosphereErrorText,
    salaryStandardErrorText,
    debutErrorText,
    educationErrorText,
    workTimeErrorText,
    salaryChangeErrorText,
    // Step4
    workplaceMatchingErrorText,
    interestRewardErrorText,
    challengeErrorText,
    expectationErrorText,
    filesErrorText,
  ]);

  // バリデーションが全て通っている場合のみisValidをtrueにして送信できるようにする
  useEffect(() => {
    // バリデーションが通っていない場合はisValidをfalseにする
    if (
      jobEntryErrorText ||
      emailErrorText ||
      employmentStatusErrorText ||
      businessTypesErrorText ||
      employmentErrorText ||
      // step2
      treatmentsErrorText ||
      culturesErrorText ||
      // step3
      atmosphereErrorText ||
      salaryStandardErrorText ||
      debutErrorText ||
      educationErrorText ||
      workTimeErrorText ||
      salaryChangeErrorText ||
      // step4
      workplaceMatchingErrorText ||
      interestRewardErrorText ||
      challengeErrorText ||
      expectationErrorText ||
      // step5
      filesErrorText
    ) {
      setIsValid(false);
      return;
    }
    setIsValid(true);
  }, [
    atmosphereErrorText,
    challengeErrorText,
    checkboxValues,
    culturesErrorText,
    debutErrorText,
    educationErrorText,
    emailErrorText,
    emailValue,
    employmentErrorText,
    employmentStatusErrorText,
    employmentValue,
    expectationErrorText,
    files,
    filesErrorText,
    interestRewardErrorText,
    jobEntryErrorText,
    jobEntryValue,
    businessTypeValue,
    businessTypesErrorText,
    radioValues,
    salaryChangeErrorText,
    salaryStandardErrorText,
    textAreaState,
    treatmentsErrorText,
    workTimeErrorText,
    workplaceMatchingErrorText,
  ]);

  return {
    isValid,
    errorTexts,
    updateClickStatus,
  };
};
